import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, Bell, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { LogoFull } from "../components/Logo";
import { PasswordInput } from "../components/PasswordInput";
import { AdminLogin } from "./AdminLogin";
import { AdminContacts } from "./AdminContacts";
import { AdminProducts } from "./AdminProducts";
import { AdminRealizzazioni } from "./AdminRealizzazioni";
import { fetchProducts, refreshProducts } from "../lib/products";
import { useSeo, SEO } from "../lib/seo";
import { getToken, setToken, me, unreadCount, changePassword, formatApiError } from "../lib/adminApi";

const UNREAD_POLL_MS = 60000;

const ChangePasswordDialog = ({ onClose }) => {
    const [cur, setCur] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (pw.length < 12) return setError("La nuova password deve avere almeno 12 caratteri.");
        if (pw !== pw2) return setError("Le due password non coincidono.");
        setBusy(true);
        try {
            await changePassword(cur, pw);
            toast.success("Password aggiornata correttamente.");
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" data-testid="admin-change-password-dialog">
            <form onSubmit={submit} className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl sm:p-8">
                <h2 className="font-display text-xl font-extrabold text-[#0B1F3A]">Cambia password</h2>
                <label className="mt-5 block text-xs font-bold uppercase tracking-wider text-[#4F5D70]">Password attuale<PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} testId="admin-pw-current" autoComplete="current-password" /></label>
                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-[#4F5D70]">Nuova password (min. 12 caratteri)<PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} testId="admin-pw-new" autoComplete="new-password" /></label>
                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-[#4F5D70]">Conferma nuova password<PasswordInput value={pw2} onChange={(e) => setPw2(e.target.value)} testId="admin-pw-confirm" autoComplete="new-password" /></label>
                {error && <p role="alert" data-testid="admin-pw-error" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-[#B91C1C]">{error}</p>}
                <div className="mt-6 flex gap-3">
                    <button type="submit" disabled={busy} data-testid="admin-pw-submit" className="flex-1 rounded-full bg-[#E30613] px-6 py-3.5 text-sm font-bold text-white disabled:opacity-60">{busy ? "Salvataggio…" : "Salva"}</button>
                    <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-6 py-3.5 text-sm font-bold text-[#0B1F3A]">Annulla</button>
                </div>
            </form>
        </div>
    );
};

const UnreadBadge = ({ count, testId, className = "" }) =>
    count > 0 ? (
        <span
            data-testid={testId}
            aria-label={`${count} richieste da leggere`}
            className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#E30613] px-1.5 text-[11px] font-extrabold leading-none text-white shadow-[0_0_0_2px_#fff] ${className}`}
        >
            {count > 99 ? "99+" : count}
        </span>
    ) : null;

export default function AdminPage() {
    useSeo(SEO.admin);
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(!!getToken());
    const [products, setProducts] = useState({});
    const [group, setGroup] = useState("contatti");
    const [unread, setUnread] = useState(0);
    const [showPw, setShowPw] = useState(false);

    const refreshUnread = useCallback(() => unreadCount().then(setUnread).catch(() => {}), []);

    useEffect(() => {
        if (!user) return undefined;
        refreshUnread();
        const id = setInterval(refreshUnread, UNREAD_POLL_MS);
        return () => clearInterval(id);
    }, [user, refreshUnread]);

    useEffect(() => {
        document.title = unread > 0 ? `(${unread}) ${SEO.admin.title}` : SEO.admin.title;
    }, [unread]);

    useEffect(() => {
        if (!getToken()) return;
        me()
            .then(setUser)
            .catch(() => setToken(null))
            .finally(() => setChecking(false));
    }, []);

    const reload = () => refreshProducts().then(setProducts).catch(() => {});
    useEffect(() => {
        if (user) fetchProducts(true).then(setProducts).catch(() => {});
    }, [user]);

    if (checking) return <div className="flex min-h-screen items-center justify-center text-sm text-[#4F5D70]">Verifica accesso…</div>;
    if (!user) return <AdminLogin onLogin={setUser} />;

    return (
        <div className="min-h-screen bg-[#F1F5F9]" data-testid="admin-dashboard">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-4 sm:px-8">
                    <LogoFull />
                    <div className="flex items-center gap-3 text-sm">
                        <button
                            type="button"
                            onClick={() => setGroup("contatti")}
                            className="relative rounded-full border border-slate-200 p-2 text-[#0B1F3A] hover:bg-slate-50"
                            aria-label="Richieste da leggere"
                            data-testid="admin-unread-bell"
                        >
                            <Bell className="h-4 w-4" />
                            <UnreadBadge count={unread} testId="admin-unread-bell-count" className="absolute -right-1.5 -top-1.5" />
                        </button>
                        <span className="hidden text-[#4F5D70] md:block">{user.email}</span>
                        <button
                            type="button"
                            onClick={() => setShowPw(true)}
                            className="rounded-full border border-slate-200 p-2.5 text-[#0B1F3A] hover:bg-slate-50 sm:px-4 sm:py-2 sm:text-xs sm:font-bold"
                            data-testid="admin-change-password-open"
                            aria-label="Cambia password"
                            title="Cambia password"
                        >
                            <KeyRound className="h-4 w-4 sm:hidden" />
                            <span className="hidden sm:inline">Cambia password</span>
                        </button>
                        <Link to="/" className="hidden rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-[#0B1F3A] hover:bg-slate-50 sm:block" data-testid="admin-view-site">
                            Vedi il sito
                        </Link>
                        <button
                            type="button"
                            onClick={() => { setToken(null); setUser(null); }}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#0B1F3A] px-3.5 py-2.5 text-xs font-bold text-white hover:bg-[#132b4f] sm:px-4 sm:py-2"
                            data-testid="admin-logout"
                        >
                            <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Esci</span>
                        </button>
                    </div>
                </div>
            </header>
            {showPw && <ChangePasswordDialog onClose={() => setShowPw(false)} />}

            <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
                <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">Area riservata</span>
                <h1 className="font-display mt-2 text-3xl font-extrabold text-[#0B1F3A]">
                    {group === "contatti" ? "Richieste dal sito" : group === "realizzazioni" ? "Realizzazioni" : "Depliant e immagini prodotto"}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-[#4F5D70]">
                    {group === "contatti"
                        ? "Tutte le richieste ricevute dal modulo contatti, come una casella di posta: il pallino rosso indica le nuove. Tocca una riga per aprire il dettaglio, rispondere, chiamare o aggiornare lo stato. Ogni lunedì alle 8:00 ricevi il riepilogo settimanale; ogni giorno lavorativo alle 8:30 il promemoria delle richieste Da richiamare."
                        : group === "realizzazioni"
                            ? "I lavori realizzati da mostrare sul sito: crea la scheda, carica le foto (volti e targhe vengono rilevati e sfocati automaticamente, con controllo e correzione manuale), collega i prodotti installati, spunta il consenso del cliente e pubblica. Ogni lavoro pubblicato ha la sua pagina /realizzazioni/… indicizzata da Google."
                            : "Per ogni prodotto: carica depliant PDF e immagini (anche trascinandoli), riordina, rinomina, scegli la foto principale, modifica la descrizione breve, nascondi o aggiungi nuovi prodotti. Le modifiche compaiono subito sul sito."}
                </p>

                <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-group-tabs">
                    {[["contatti", "Richieste"], ["christ", "Autolavaggio Christ"], ["officina", "Autoriparatore"], ["realizzazioni", "Realizzazioni"]].map(([g, l]) => (
                        <button
                            key={g}
                            type="button"
                            onClick={() => setGroup(g)}
                            data-testid={`admin-tab-${g}`}
                            className={`relative rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${group === g ? "bg-[#0B1F3A] text-white" : "bg-white text-[#0B1F3A] hover:bg-slate-100"}`}
                        >
                            {l}
                            {g === "contatti" && <UnreadBadge count={unread} testId="admin-unread-tab-count" className="absolute -right-1.5 -top-1.5" />}
                        </button>
                    ))}
                </div>

                {group === "contatti" ? (
                    <AdminContacts onChanged={refreshUnread} />
                ) : group === "realizzazioni" ? (
                    <AdminRealizzazioni products={products} />
                ) : (
                    <AdminProducts group={group} products={products} onChanged={reload} />
                )}
            </main>
        </div>
    );
}
